import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/hooks/use-sidebar";
import logoPath from "@assets/Asset 1_1750308719655.png";
import {
  LayoutDashboard,
  AppWindow,
  Key,
  LineChart,
  DollarSign,
  Users,
  Settings,
  HelpCircle,
  FileText,
  PackageCheck,
  CreditCard,
  Upload,
  Wallet,
  Menu,
  X,
  UserRound,
  UsersRound,
  Building,
  ShieldAlert,
  BarChart3,
  Globe,
  AlertCircle,
  Receipt,
  ChevronLeft,
  ChevronRight,
  Mail,
} from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// All navigation items - MAIN section
const allMainNavItems = [
  { name: "Dashboard", icon: LayoutDashboard, path: "/", adminOnly: false },
  { name: "Applications", icon: AppWindow, path: "/applications", adminOnly: false, tourId: "nav-applications" },
  { name: "Licenses", icon: Key, path: "/licenses", adminOnly: false, tourId: "nav-licenses" },
  { name: "Billing History", icon: Receipt, path: "/billing-history", adminOnly: true },
  { name: "Payment Cards", icon: CreditCard, path: "/payment-cards", adminOnly: true },
  { name: "Usage Analytics", icon: LineChart, path: "/analytics", adminOnly: true },
  { name: "Cost Management", icon: DollarSign, path: "/cost-management", adminOnly: true },
  { name: "People", icon: UsersRound, path: "/people", adminOnly: true, tourId: "nav-people" },
  { name: "Team Management", icon: Users, path: "/team", adminOnly: true, tourId: "nav-team" },
];

// ADMINISTRATION section
// Settings is available to every user (e.g. to request an email change from their Profile
// tab) — the Settings page itself restricts which tabs a non-admin can see.
const adminNavItems = [
  { name: "Data Import", icon: Upload, path: "/data-import", adminOnly: true },
  { name: "Settings", icon: Settings, path: "/settings", adminOnly: false },
  { name: "Email Approvals", icon: Mail, path: "/email-approvals", adminOnly: true },
  { name: "Subscription", icon: Wallet, path: "/subscription", adminOnly: true },
];

// SUPER ADMIN section
const superAdminNavItems = [
  { name: "Organizations", icon: Building, path: "/admin/tenants" },
  { name: "System Health", icon: BarChart3, path: "/admin/system" },
  { name: "Subscriptions", icon: CreditCard, path: "/admin/subscriptions" },
  { name: "Global Settings", icon: Globe, path: "/admin/settings" },
  { name: "Security", icon: ShieldAlert, path: "/admin/security" },
  { name: "Alerts", icon: AlertCircle, path: "/admin/alerts" },
];

// SUPPORT section
const supportNavItems = [
  { name: "Help Center", icon: HelpCircle, path: "/help" },
];

type NavItem = { name: string; icon: React.ElementType; path: string; tourId?: string };

function NavButton({
  item,
  active,
  collapsed,
  activeClass,
  hoverClass,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  activeClass: string;
  hoverClass: string;
}) {
  const Icon = item.icon;

  const button = (
    <Link href={item.path}>
      <button
        data-tour={item.tourId}
        className={cn(
          "flex items-center w-full transition-all duration-200 text-left group rounded-xl button-modern",
          collapsed
            ? "justify-center px-0 py-3 mx-auto w-10 h-10"
            : "px-4 py-3 text-sm",
          active
            ? activeClass
            : cn("text-slate-700", collapsed ? "hover:bg-slate-100" : hoverClass)
        )}
      >
        <Icon
          className={cn(
            "h-4 w-4 flex-shrink-0",
            collapsed ? "" : "mr-3"
          )}
        />
        {!collapsed && <span className="font-medium truncate">{item.name}</span>}
      </button>
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {item.name}
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
}

function NavigationContent({ collapsed = false }: { collapsed?: boolean }) {
  const [location] = useLocation();
  const { user, isSuperAdmin } = useAuth();
  const isAdmin = user?.role === "admin";
  const mainNavItems = allMainNavItems.filter(item => isAdmin || !item.adminOnly);
  const visibleAdminNavItems = adminNavItems.filter(item => isAdmin || !item.adminOnly);

  const getUserInitials = () => {
    if (!user?.fullName) return "U";
    const parts = user.fullName.split(" ");
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}` : parts[0][0];
  };

  return (
    <TooltipProvider>
      <div
        className={cn(
          "p-3 border-b border-slate-200/60 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 backdrop-blur-sm flex items-center",
          collapsed ? "justify-center py-4" : "justify-between px-5 py-4"
        )}
      >
        {!collapsed && (
          <img
            src={logoPath}
            alt="SaaSGama"
            className="h-9 w-auto object-contain animate-fade-in"
          />
        )}
        {collapsed && (
          <img
            src={logoPath}
            alt="SaaSGama"
            className="h-8 w-8 object-contain"
          />
        )}
      </div>

      <nav
        className={cn(
          "flex-1 overflow-y-auto overflow-x-hidden scrollbar-hide py-4",
          collapsed ? "px-2" : "px-4"
        )}
        data-sidebar
        data-preserve-scroll
      >
        {/* MAIN */}
        <div className="mb-6">
          {!collapsed && (
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2">
              Main
            </p>
          )}
          {collapsed && <div className="h-px bg-slate-100 mb-3" />}
          <ul className="space-y-1">
            {mainNavItems.map(item => (
              <li key={item.path}>
                <NavButton
                  item={item}
                  active={location === item.path || location === item.path + "/"}
                  collapsed={collapsed}
                  activeClass="bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold shadow-lg shadow-blue-500/25"
                  hoverClass="hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 hover:shadow-md hover:text-slate-900 modern-card"
                />
              </li>
            ))}
          </ul>
        </div>

        {/* SUPER ADMIN */}
        {isSuperAdmin && (
          <div className="mb-6">
            {!collapsed && (
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 px-2">
                <span className="text-amber-600 mr-1">•</span>Super Admin
              </p>
            )}
            {collapsed && <div className="h-px bg-slate-100 mb-3" />}
            <ul className="space-y-1">
              {superAdminNavItems.map(item => (
                <li key={item.path}>
                  <NavButton
                    item={item}
                    active={location === item.path || location === item.path + "/"}
                    collapsed={collapsed}
                    activeClass="bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium shadow-lg shadow-amber-500/25"
                    hoverClass="hover:bg-gradient-to-r hover:from-amber-50 hover:to-orange-50 hover:shadow-md"
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* ADMINISTRATION */}
        {visibleAdminNavItems.length > 0 && (
          <div className="mb-6">
            {!collapsed && (
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2">
                {isAdmin ? "Administration" : "Settings"}
              </p>
            )}
            {collapsed && <div className="h-px bg-slate-100 mb-3" />}
            <ul className="space-y-1">
              {visibleAdminNavItems.map(item => (
                <li key={item.path}>
                  <NavButton
                    item={item}
                    active={location === item.path}
                    collapsed={collapsed}
                    activeClass="bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold shadow-lg shadow-emerald-500/25 modern-card"
                    hoverClass="hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 hover:shadow-md hover:text-slate-900 modern-card"
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* SUPPORT */}
        <div className="mb-4">
          {!collapsed && (
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 px-2">
              Support
            </p>
          )}
          {collapsed && <div className="h-px bg-slate-100 mb-3" />}
          <ul className="space-y-1">
            {supportNavItems.map(item => (
              <li key={item.path}>
                <NavButton
                  item={item}
                  active={location === item.path}
                  collapsed={collapsed}
                  activeClass="bg-gradient-to-r from-blue-500 to-cyan-500 text-white"
                  hoverClass="hover:bg-gradient-to-r hover:from-slate-50 hover:to-gray-50 hover:shadow-md hover:text-slate-900 modern-card"
                />
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* User footer */}
      {user && (
        <div
          className={cn(
            "mt-auto border-t border-slate-200/60 bg-gradient-to-r from-slate-50/80 to-blue-50/50 backdrop-blur-sm",
            collapsed ? "p-3 flex justify-center" : "p-4"
          )}
        >
          {collapsed ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Avatar className="ring-2 ring-slate-200 ring-offset-2 cursor-default">
                  <AvatarFallback className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg font-bold text-sm">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p className="font-medium">{user.fullName}</p>
                <p className="text-xs text-muted-foreground">{user.email}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <div className="flex items-center">
              <Avatar className="ring-2 ring-slate-200 ring-offset-2">
                <AvatarFallback className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg font-bold">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-800 truncate">{user.fullName}</p>
                <p className="text-xs text-slate-600 truncate">{user.email}</p>
                <span
                  className={`inline-block mt-1 text-xs px-2 py-1 rounded-lg shadow-sm font-medium ${
                    user.role === "admin"
                      ? "bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 border border-emerald-200"
                      : "bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 border border-slate-200"
                  }`}
                >
                  {user.role}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </TooltipProvider>
  );
}

export function MobileSidebar() {
  const { mobileOpen, setMobileOpen } = useSidebar();

  return (
    <div className="md:hidden">
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="text-slate-600 hover:bg-slate-100 rounded-xl transition-all duration-200 hover:scale-105 button-modern"
          >
            <Menu className="h-6 w-6" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-gradient-to-b from-slate-50 to-blue-50/30 border-slate-200/60 shadow-2xl animate-slide-up flex flex-col"
          data-sheet-content
          data-preserve-scroll
          onInteractOutside={(event) => {
            // The setup tour's overlay/spotlight render in the "#react-joyride-portal"
            // portal, while its tooltip (with the Next/Skip/Done buttons) renders in a
            // separate react-floater portal (class "__floater", id "react-joyride-step-N")
            // — both outside this sheet, so interacting with either would otherwise
            // register as an outside interaction and close the menu mid-tour.
            const target = event.target as HTMLElement | null;
            if (target?.closest("#react-joyride-portal") || target?.closest(".__floater")) {
              event.preventDefault();
            }
          }}
        >
          <NavigationContent collapsed={false} />
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default function Sidebar() {
  const isMobile = useMobile();
  const { collapsed, toggle } = useSidebar();

  if (isMobile) return null;

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-20 bg-white/95 backdrop-blur-sm shadow-lg flex-shrink-0 hidden md:flex flex-col border-r border-slate-200/60 modern-card transition-all duration-300 ease-in-out",
        collapsed ? "w-16" : "w-64"
      )}
      data-sidebar
    >
      <NavigationContent collapsed={collapsed} />

      {/* Toggle button */}
      <button
        onClick={toggle}
        className="absolute -right-3 top-16 z-30 h-6 w-6 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center hover:bg-slate-50 hover:shadow-lg transition-all duration-200"
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-slate-600" />
        ) : (
          <ChevronLeft className="h-3.5 w-3.5 text-slate-600" />
        )}
      </button>
    </aside>
  );
}
